import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, User, Ban, Stethoscope } from "lucide-react";

export default function StepMinorenni({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const s4 = dvr.steps?.step4_mansioni || [];
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    setAiLoading(true);
    try {
      const payload = {
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
        numero_minorenni: s1.numero_minorenni,
        mansioni_previste_per_minori: s4.map((m) => m.mansione),
        note,
      };
      const r = await api.post("/dvr/ai/minorenni", payload, { timeout: 120000 });
      onChange({ ...r.data, _note: note });
      toast.success("Valutazione minorenni completata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="step-minorenni">
      <p className="text-sm text-muted-foreground">
        Valutazione ex <b>D.Lgs. 345/1999</b> (tutela lavoro minorile) + D.Lgs. 81/08 art. 41 c.2
        (sorveglianza sanitaria preventiva minori). Minorenni in azienda: <b>{s1.numero_minorenni || 0}</b>.
        Il modulo si compila anche in assenza di minori — utile per assunzioni future.
      </p>
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (es. tirocini formativi, alternanza scuola-lavoro…)"
        data-testid="mn-note"
      />
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="mn-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <User className="w-4 h-4 mr-2" />}
        Valuta minorenni
      </Button>

      {d.lavori_vietati_ai_minori?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-1"><Ban className="w-4 h-4 text-rose-600" /> Lavori vietati ai minori</h3>
          <ul className="text-sm mt-1 space-y-1">
            {d.lavori_vietati_ai_minori.map((l, i) => (
              <li key={`lv-${l.tipologia || i}-${i}`} className="border-l-2 border-rose-300 pl-2">
                <b>{l.tipologia}</b>: {l.descrizione}
                <div className="text-[11px] text-muted-foreground">Rif: {l.normativa}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.eccezioni_formative?.condizioni?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm">
          <b>Eccezioni formative (apprendistato/scuola):</b>
          <ul className="mt-1 space-y-0.5">
            {d.eccezioni_formative.condizioni.map((c, i) => <li key={`ec-${i}-${(c || "").slice(0, 20)}`}>• {c}</li>)}
          </ul>
          <div className="text-[11px] text-muted-foreground mt-1">Rif: {d.eccezioni_formative.normativa}</div>
        </div>
      )}

      {d.orario_massimo && (
        <div className="border border-border rounded-md p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm bg-muted/20">
          <div><div className="text-[11px] uppercase text-muted-foreground">Giornaliero</div><b>{d.orario_massimo.giornaliero_h}h</b></div>
          <div><div className="text-[11px] uppercase text-muted-foreground">Settimanale</div><b>{d.orario_massimo.settimanale_h}h</b></div>
          <div><div className="text-[11px] uppercase text-muted-foreground">Riposi</div>{d.orario_massimo.riposi}</div>
          <div><div className="text-[11px] uppercase text-muted-foreground">Notturno</div>{d.orario_massimo.notturno}</div>
        </div>
      )}

      {d.sorveglianza_sanitaria && (
        <div className="border border-rose-200 bg-rose-50 rounded-md p-3 text-sm">
          <div className="flex items-center gap-1 font-semibold"><Stethoscope className="w-4 h-4" /> Sorveglianza sanitaria</div>
          <div className="mt-1">
            Preventiva: {d.sorveglianza_sanitaria.obbligo_preventiva ? "OBBLIGATORIA" : "non richiesta"} ·
            Periodicità: <b>{d.sorveglianza_sanitaria.periodicita}</b>
          </div>
          {d.sorveglianza_sanitaria.esami?.length > 0 && (
            <div className="text-xs mt-1">Esami: {d.sorveglianza_sanitaria.esami.join(", ")}</div>
          )}
          <div className="text-[11px] text-muted-foreground mt-1">Rif: {d.sorveglianza_sanitaria.normativa}</div>
        </div>
      )}

      {d.formazione_specifica && (
        <div className="text-sm">
          <b>Formazione:</b> {d.formazione_specifica.ore} — {d.formazione_specifica.modalita}
          <div className="text-[11px] text-muted-foreground">Rif: {d.formazione_specifica.normativa}</div>
        </div>
      )}
      {d.documenti_da_produrre?.length > 0 && (
        <div className="text-xs text-muted-foreground">Documenti: {d.documenti_da_produrre.join(" · ")}</div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
